import {
  actionCreators as FeedItemsActions,
  ActionTypes as FeedItemsActionTypes,
  FeedItemScope,
  IDeleteFeedItemSuccessAction,
  ILoadFeedItemOptions,
  ILoadFeedItemsSuccessAction,
  ISaveEditFeedItemSuccessAction,
  loadFeedItemsSaga,
} from '@app/components/FeedItem/modules/feedItems'
import {
  FeedItemEditorActionTypes,
  ICreateFeedItemSuccessAction,
} from '@app/components/FeedItemEditor/modules/feedItemEditor'
import {
  ActivityFeedItem,
  IActivityFeedItem,
} from '@app/models/ActivityFeedItem'
import { IFeedItem } from '@app/models/FeedItem'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { call, fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface IActivityFeedState {
  readonly entityId?: string
  readonly error?: string
  readonly filterText?: string
  readonly isLoading: boolean
  readonly isLoadingNewItems: boolean
  readonly itemChangedIndex?: number
  readonly items: List<IActivityFeedItem>
  readonly newItems: List<IActivityFeedItem>
  readonly scope: FeedItemScope
  readonly totalItems: number
}

const StateRecord = Record({
  entityId: undefined,
  error: undefined,
  filterText: '',
  isLoading: false,
  isLoadingNewItems: false,
  itemChangedIndex: undefined,
  items: List<ActivityFeedItem>(),
  newItems: List<ActivityFeedItem>(),
  scope: FeedItemScope.Mine,
  totalItems: 0,
})

export class ActivityFeedState extends StateRecord
  implements IActivityFeedState {
  public static fromJS(initialState?: Partial<IActivityFeedState>) {
    if (!initialState) {
      return new ActivityFeedState()
    }

    const { items, newItems, ...rest } = initialState
    return new ActivityFeedState({
      items: items
        ? List(items.map((i) => ActivityFeedItem.fromJS(i)))
        : List<ActivityFeedItem>(),
      newItems: newItems
        ? List(newItems.map((i) => ActivityFeedItem.fromJS(i)))
        : List<ActivityFeedItem>(),
      ...rest,
    })
  }

  public readonly entityId?: string
  public readonly error?: string
  public readonly filterText?: string
  public readonly isLoading: boolean
  public readonly isLoadingNewItems: boolean
  public readonly itemChangedIndex?: number
  public readonly items: List<ActivityFeedItem>
  public readonly newItems: List<ActivityFeedItem>
  public readonly scope: FeedItemScope
  public readonly totalItems: number

  constructor(props?: Partial<ActivityFeedState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActivityFeedActionTypes {
  FeedItemLayoutChanged = 'ACTIVITY_FEED::FEED_ITEM_LAYOUT_CHANGED',
  LoadActivityFeed = 'ACTIVITY_FEED::LOAD_ACTIVITY_FEED',
  LoadActivityFeedFailure = 'ACTIVITY_FEED::LOAD_ACTIVITY_FEED_FAILURE',
  LoadActivityFeedRequest = 'ACTIVITY_FEED::LOAD_ACTIVITY_FEED_REQUEST',
  LoadActivityFeedSuccess = 'ACTIVITY_FEED::LOAD_ACTIVITY_FEED_SUCCESS',
  LoadNewActivityFeed = 'ACTIVITY_FEED::LOAD_NEW_ACTIVITY_FEED',
  LoadNewActivityFeedFailure = 'ACTIVITY_FEED::LOAD_NEW_ACTIVITY_FEED_FAILURE',
  LoadNewActivityFeedRequest = 'ACTIVITY_FEED::LOAD_NEW_ACTIVITY_FEED_REQUEST',
  LoadNewActivityFeedSuccess = 'ACTIVITY_FEED::LOAD_NEW_ACTIVITY_FEED_SUCCESS',
  ResetActivityFeed = 'ACTIVITY_FEED::RESET_ACTIVITY_FEED',
  ResetItemChangedIndex = 'ACTIVITY_FEED::RESET_ITEM_CHANGED_INDEX',
  SetEntityId = 'ACTIVITY_FEED::SET_ENTITY_ID',
  SetFilter = 'ACTIVITY_FEED::SET_FILTER',
  SetScope = 'ACTIVITY_FEED::SET_SCOPE',
  ShowNewItems = 'ACTIVITY_FEED::SHOW_NEW_ITEMS',
}

interface IFeedItemLayoutChangedAction {
  feedItemId: string
  index: number
  type: ActivityFeedActionTypes.FeedItemLayoutChanged
}

interface ILoadActivityFeedAction {
  append: boolean
  filter?: string
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: ActivityFeedActionTypes.LoadActivityFeed
}

interface ILoadActivityFeedFailureAction {
  error: string
  type: ActivityFeedActionTypes.LoadActivityFeedFailure
}

interface ILoadActivityFeedRequestAction {
  type: ActivityFeedActionTypes.LoadActivityFeedRequest
}

interface ILoadActivityFeedSuccessAction {
  append: boolean
  items: IFeedItem[]
  totalItems: number
  type: ActivityFeedActionTypes.LoadActivityFeedSuccess
}

interface ILoadNewActivityFeedAction {
  type: ActivityFeedActionTypes.LoadNewActivityFeed
}

interface ILoadNewActivityFeedFailureAction {
  error: string
  type: ActivityFeedActionTypes.LoadNewActivityFeedFailure
}

interface ILoadNewActivityFeedRequestAction {
  type: ActivityFeedActionTypes.LoadNewActivityFeedRequest
}

interface ILoadNewActivityFeedSuccessAction {
  items: IFeedItem[]
  totalItems: number
  type: ActivityFeedActionTypes.LoadNewActivityFeedSuccess
}

interface IResetActivityFeedAction {
  type: ActivityFeedActionTypes.ResetActivityFeed
}

interface IResetItemChangedIndexAction {
  type: ActivityFeedActionTypes.ResetItemChangedIndex
}

interface ISetEntityIdAction {
  entityId?: string
  type: ActivityFeedActionTypes.SetEntityId
}

interface ISetFilterAction {
  filter?: string
  type: ActivityFeedActionTypes.SetFilter
}

interface ISetScopeAction {
  scope: FeedItemScope
  type: ActivityFeedActionTypes.SetScope
}

interface IShowNewItemsAction {
  type: ActivityFeedActionTypes.ShowNewItems
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ICreateFeedItemSuccessAction
  | IDeleteFeedItemSuccessAction
  | IFeedItemLayoutChangedAction
  | ILoadActivityFeedFailureAction
  | ILoadActivityFeedRequestAction
  | ILoadActivityFeedSuccessAction
  | ILoadNewActivityFeedFailureAction
  | ILoadNewActivityFeedRequestAction
  | ILoadNewActivityFeedSuccessAction
  | IResetActivityFeedAction
  | IResetItemChangedIndexAction
  | ISaveEditFeedItemSuccessAction
  | ISetEntityIdAction
  | ISetFilterAction
  | ISetScopeAction
  | IShowNewItemsAction

// -------------------------
// ACTION CREATORS
// -------------------------

function feedItemLayoutChanged(
  index: number,
  feedItemId: string,
): IFeedItemLayoutChangedAction {
  return {
    feedItemId,
    index,
    type: ActivityFeedActionTypes.FeedItemLayoutChanged,
  }
}

function loadActivityFeed(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  filter?: string,
  loadComplete?: () => void,
): ILoadActivityFeedAction {
  return {
    append,
    filter,
    loadComplete,
    startIndex,
    stopIndex,
    type: ActivityFeedActionTypes.LoadActivityFeed,
  }
}

function loadActivityFeedFailure(
  error: string,
): ILoadActivityFeedFailureAction {
  return {
    error,
    type: ActivityFeedActionTypes.LoadActivityFeedFailure,
  }
}

function loadActivityFeedRequest(): ILoadActivityFeedRequestAction {
  return {
    type: ActivityFeedActionTypes.LoadActivityFeedRequest,
  }
}

function loadActivityFeedSuccess(
  items: IFeedItem[],
  append: boolean,
  totalItems: number,
): ILoadActivityFeedSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: ActivityFeedActionTypes.LoadActivityFeedSuccess,
  }
}

function loadNewActivityFeed(): ILoadNewActivityFeedAction {
  return {
    type: ActivityFeedActionTypes.LoadNewActivityFeed,
  }
}

function loadNewActivityFeedFailure(
  error: string,
): ILoadNewActivityFeedFailureAction {
  return {
    error,
    type: ActivityFeedActionTypes.LoadNewActivityFeedFailure,
  }
}

function loadNewActivityFeedRequest(): ILoadNewActivityFeedRequestAction {
  return {
    type: ActivityFeedActionTypes.LoadNewActivityFeedRequest,
  }
}

function loadNewActivityFeedSuccess(
  items: IFeedItem[],
  totalItems: number,
): ILoadNewActivityFeedSuccessAction {
  return {
    items,
    totalItems,
    type: ActivityFeedActionTypes.LoadNewActivityFeedSuccess,
  }
}

function resetActivityFeed(): IResetActivityFeedAction {
  return { type: ActivityFeedActionTypes.ResetActivityFeed }
}

function resetItemChangedIndex(): IResetItemChangedIndexAction {
  return { type: ActivityFeedActionTypes.ResetItemChangedIndex }
}

function setEntityId(entityId?: string): ISetEntityIdAction {
  return {
    entityId,
    type: ActivityFeedActionTypes.SetEntityId,
  }
}

function setFilter(filter?: string): ISetFilterAction {
  return {
    filter,
    type: ActivityFeedActionTypes.SetFilter,
  }
}

function setScope(scope: FeedItemScope): ISetScopeAction {
  return {
    scope,
    type: ActivityFeedActionTypes.SetScope,
  }
}

function showNewItems(): IShowNewItemsAction {
  return { type: ActivityFeedActionTypes.ShowNewItems }
}

export const actionCreators = {
  feedItemLayoutChanged,
  loadActivityFeed,
  loadNewActivityFeed,
  resetActivityFeed,
  resetItemChangedIndex,
  setEntityId,
  setFilter,
  setScope,
  showNewItems,
}

// -------------------------
// REDUCER
// -------------------------

function getNewItemChangedIndex(state: ActivityFeedState, index: number) {
  if (state.itemChangedIndex === undefined) {
    return index
  } else {
    return Math.min(state.itemChangedIndex, index)
  }
}

function convertArrayToActivityFeedItemList(
  items: IFeedItem[],
): List<ActivityFeedItem> {
  const newItems = items.map(convertToActivityFeedItem)
  return List(newItems)
}

function convertToActivityFeedItem(item: IFeedItem): ActivityFeedItem {
  return new ActivityFeedItem({
    dateCreated: item.dateCreated,
    id: item.id,
    lastActivityTime: item.lastActivityTime,
  })
}

export const reducer: Reducer<ActivityFeedState> = (
  state: ActivityFeedState = new ActivityFeedState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case FeedItemEditorActionTypes.CreateFeedItemSuccess:
      return state
        .set(
          'items',
          state.items.insert(0, convertToActivityFeedItem(action.feedItem)),
        )
        .set('totalItems', state.totalItems + 1) as ActivityFeedState

    case FeedItemsActionTypes.SaveEditFeedItemSuccess: {
      const itemIndex = state.items.findIndex(
        (i) => i !== undefined && i.id === action.feedItem.id,
      )
      if (itemIndex >= 0) {
        return state.setIn(['items', itemIndex], convertToActivityFeedItem(action.feedItem)) as ActivityFeedState
      } else {
        return state
          .set(
            'items',
            state.items.insert(0, convertToActivityFeedItem(action.feedItem)),
          )
          .set('totalItems', state.totalItems + 1) as ActivityFeedState
      }
    }

    case FeedItemsActionTypes.DeleteFeedItemSuccess:
      const deletedIndex = state.items.findIndex(
        (i) => i !== undefined && i.id === action.feedItemId,
      )
      if (deletedIndex >= 0) {
        return state
          .set('itemChangedIndex', getNewItemChangedIndex(state, deletedIndex))
          .set('items', state.items.delete(deletedIndex))
          .set('totalItems', state.totalItems - 1) as ActivityFeedState
      } else {
        return state
      }

    case ActivityFeedActionTypes.FeedItemLayoutChanged: {
      const listFeedItemId: string | undefined = state.getIn([
        'items',
        action.index,
        'id',
      ])
      if (listFeedItemId === action.feedItemId) {
        return state.set(
          'itemChangedIndex',
          getNewItemChangedIndex(state, action.index),
        ) as ActivityFeedState
      } else {
        return state
      }
    }

    case ActivityFeedActionTypes.LoadActivityFeedFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as ActivityFeedState

    case ActivityFeedActionTypes.LoadActivityFeedRequest:
      return state.set('isLoading', true) as ActivityFeedState

    case ActivityFeedActionTypes.LoadActivityFeedSuccess:
      const itemList = convertArrayToActivityFeedItemList(action.items)
      if (action.append) {
        return state
          .set('error', undefined)
          .set('isLoading', false)
          .set('items', state.items.concat(itemList)) as ActivityFeedState
      } else {
        return state
          .set('error', undefined)
          .set('isLoading', false)
          .set('items', itemList)
          .set('itemChangedIndex', undefined)
          .set('newItems', List<ActivityFeedItem>())
          .set('totalItems', action.totalItems) as ActivityFeedState
      }

    case ActivityFeedActionTypes.LoadNewActivityFeedFailure:
      return state
        .set('error', action.error)
        .set('isLoadingNewItems', false) as ActivityFeedState

    case ActivityFeedActionTypes.LoadNewActivityFeedRequest:
      return state.set('isLoadingNewItems', true) as ActivityFeedState

    case ActivityFeedActionTypes.LoadNewActivityFeedSuccess: {
      const newItems = action.items.filter(
        (i) =>
          state.items.findIndex((si) => si !== undefined && si.id === i.id) === -1,
      )
      const newItemsList = convertArrayToActivityFeedItemList(newItems)
      const existingNewItemsWithoutDuplicates = state.newItems
        .filter((i) => {
          return (
            i !== undefined &&
            newItemsList.findIndex((ni) => ni !== undefined && i.id === ni.id) ===
              -1
          )
        })
        .toList()

      return state
        .set('isLoadingNewItems', false)
        .set(
          'newItems',
          newItemsList.concat(existingNewItemsWithoutDuplicates).toList(),
        ) as ActivityFeedState
    }

    case ActivityFeedActionTypes.ResetActivityFeed:
      return state
        .set('itemChangedIndex', undefined)
        .set('items', List<ActivityFeedItem>())
        .set('newItems', List<ActivityFeedItem>())
        .set('totalItems', 0) as ActivityFeedState

    case ActivityFeedActionTypes.ResetItemChangedIndex:
      return state.set('itemChangedIndex', undefined) as ActivityFeedState

    case ActivityFeedActionTypes.SetEntityId:
      return state.set('entityId', action.entityId) as ActivityFeedState

    case ActivityFeedActionTypes.SetFilter:
      return state.set('filterText', action.filter) as ActivityFeedState

    case ActivityFeedActionTypes.SetScope:
      return state.set('scope', action.scope) as ActivityFeedState

    case ActivityFeedActionTypes.ShowNewItems: {
      const lastLoadedItemTime =
        state.items.size > 0 ? state.items.first().lastActivityTime : 0
      const newItemsCount = state.newItems.count(
        (i) => i !== undefined && i.dateCreated > lastLoadedItemTime,
      )
      const existingItems = state.items
        .filter((i) => {
          return (
            i !== undefined &&
            state.newItems.findIndex(
              (ni) => ni !== undefined && ni.id === i.id,
            ) === -1
          )
        })
        .toList()
      return state
        .set('itemChangedIndex', 0)
        .set('items', state.newItems.concat(existingItems).toList())
        .set('newItems', List<ActivityFeedItem>())
        .set(
          'totalItems',
          state.totalItems + newItemsCount,
        ) as ActivityFeedState
    }

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadActivityFeedSaga(action: ILoadActivityFeedAction) {
  try {
    yield put(loadActivityFeedRequest())

    const activityFeedState: ActivityFeedState = yield select(
      (state: ApplicationState) => state.activityFeed,
    )

    const loadOptions: ILoadFeedItemOptions = {
      filter: activityFeedState.filterText,
      scope: activityFeedState.scope,
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      loadOptions.count = count
    }

    if (action.append === true && activityFeedState.items.size > 0) {
      const lastItem = activityFeedState.items.last()
      loadOptions.before = lastItem.lastActivityTime
    }

    if (activityFeedState.entityId) {
      loadOptions.entityId = activityFeedState.entityId
    }

    const result = yield call(
      loadFeedItemsSaga,
      FeedItemsActions.loadFeedItems(loadOptions),
    )
    const resultAction = result as ILoadFeedItemsSuccessAction

    if (resultAction) {
      yield put(
        loadActivityFeedSuccess(
          resultAction.feedItems,
          action.append,
          resultAction.total,
        ),
      )
      if (action.loadComplete) {
        action.loadComplete()
      }
    } else {
      yield put(
        loadActivityFeedFailure(
          'Failed to get LoadFeedItemSuccessAction result',
        ),
      )
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
    yield put(loadActivityFeedFailure(err.message))
  }
}

function* loadNewActivityFeedSaga(action: ILoadNewActivityFeedAction) {
  try {
    yield put(loadNewActivityFeedRequest())

    const activityFeedState: ActivityFeedState = yield select(
      (state: ApplicationState) => state.activityFeed,
    )

    const loadOptions: ILoadFeedItemOptions = {
      filter: activityFeedState.filterText,
      scope: activityFeedState.scope,
    }

    if (activityFeedState.items.size > 0) {
      const firstItem = activityFeedState.items.maxBy((item) => item && item.lastActivityTime)
      loadOptions.since = firstItem.lastActivityTime
    }

    if (activityFeedState.newItems.size > 0) {
      const firstItem = activityFeedState.newItems.maxBy((item) => item && item.lastActivityTime)
      loadOptions.since = Math.max(
        loadOptions.since || 0,
        firstItem.lastActivityTime,
      )
    }

    if (activityFeedState.entityId) {
      loadOptions.entityId = activityFeedState.entityId
    }

    const result = yield call(
      loadFeedItemsSaga,
      FeedItemsActions.loadFeedItems(loadOptions),
    )
    const resultAction = result as ILoadFeedItemsSuccessAction

    if (resultAction) {
      yield put(
        loadNewActivityFeedSuccess(resultAction.feedItems, resultAction.total),
      )
    } else {
      yield put(loadNewActivityFeedFailure('Failed to get new items'))
    }
  } catch (err) {
    yield put(loadNewActivityFeedFailure(err.message))
  }
}

function* watchLoadActivityFeed() {
  yield takeLatest(
    ActivityFeedActionTypes.LoadActivityFeed,
    loadActivityFeedSaga,
  )
}

function* watchLoadNewActivityFeed() {
  yield takeLatest(
    ActivityFeedActionTypes.LoadNewActivityFeed,
    loadNewActivityFeedSaga,
  )
}

export function* activityFeedRootSaga() {
  yield fork(watchLoadActivityFeed)
  yield fork(watchLoadNewActivityFeed)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): ActivityFeedState =>
  state.activityFeed

export const selectEntityId = () =>
  createSelector(
    selectModuleState(),
    (activityFeed) => activityFeed.entityId,
  )

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (activityFeed) => activityFeed.error,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (activityFeed) => activityFeed.isLoading,
  )

export const selectIsLoadingNewItems = () =>
  createSelector(
    selectModuleState(),
    (activityFeed) => activityFeed.isLoadingNewItems,
  )

export const selectItemChangedIndex = () =>
  createSelector(
    selectModuleState(),
    (activityFeed) => activityFeed.itemChangedIndex,
  )

export const selectItems = () =>
  createSelector(
    selectModuleState(),
    (activityFeed) => activityFeed.items,
  )

export const selectNewItems = () =>
  createSelector(
    selectModuleState(),
    (activityFeed) => activityFeed.newItems,
  )

export const selectFilter = () =>
  createSelector(
    selectModuleState(),
    (activityFeed) => activityFeed.filterText,
  )

export const selectScope = () =>
  createSelector(
    selectModuleState(),
    (activityFeed) => activityFeed.scope,
  )

export const selectTotalItems = () =>
  createSelector(
    selectModuleState(),
    (activityFeed) => activityFeed.totalItems,
  )
